import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { Company, CompanyService } from '../../services/company.service';
import { CompanyContextService } from '../../services/company-context.service';
import { companyInitials } from '../../utils/company-display.util';

interface CompanyTab {
  label: string;
  path: string;
}

/**
 * Shell for everything scoped to a single company: impersonation, location settings (lanes,
 * tunnel, Way), bulk import and the audit log. Owns the company lookup so child tabs never need their own
 * company picker — the route param is the selection.
 */
@Component({
  selector: 'app-company-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './company-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-detail.component.scss'],
})
export class CompanyDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly companyContext = inject(CompanyContextService);

  company: Company | null = null;
  companyId = '';
  loading = true;
  errorMessage = '';

  readonly companyInitials = companyInitials;
  readonly tabs: CompanyTab[] = [
    { label: 'Overview', path: 'overview' },
    { label: 'Impersonate', path: 'impersonate' },
    { label: 'Location', path: 'location' },
    { label: 'Customer Migration', path: 'bulk-import' },
    { label: 'Feedback Configuration', path: 'feedback-configuration' },
    { label: 'Membership Plan Changes', path: 'membership-plan-changes' },
    { label: 'Cancellation & Refund', path: 'cancellation-refund' },
    { label: 'Membership Pause Events', path: 'membership-pause-events' },
    { label: 'Features', path: 'features' },
    { label: 'Audit Log', path: 'audit-log' },
  ];

  private paramSub?: Subscription;
  private companySub?: Subscription;

  ngOnInit(): void {
    this.paramSub = this.route.paramMap.subscribe(params => {
      const id = params.get('companyId') || '';
      if (id !== this.companyId) {
        this.companyId = id;
        this.loadCompany();
      }
    });
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
    this.companySub?.unsubscribe();
    this.companyContext.clear();
  }

  logoUrl(): string | null {
    const logo = (this.company?.logo || '').trim();
    return logo || null;
  }

  onLogoError(): void {
    if (this.company) {
      this.company = { ...this.company, logo: undefined };
    }
  }

  loadCompany(): void {
    this.companySub?.unsubscribe();
    if (!this.companyId) {
      this.loading = false;
      this.errorMessage = 'No company selected.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.companySub = this.companyService.getCompany(this.companyId).subscribe({
      next: res => {
        this.loading = false;
        if (res.status && res.data) {
          this.company = res.data;
          this.companyContext.set(res.data);
          return;
        }
        this.company = null;
        this.companyContext.clear();
        this.errorMessage = res.message || 'Unable to load this company.';
      },
      error: err => {
        this.loading = false;
        this.company = null;
        this.companyContext.clear();
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load this company.';
      },
    });
  }
}
